import React, { useEffect } from 'react';
import useEditorStore from '../../../stores/useEditorStore';
import { ColorPicker, SliderInput } from '../../common';
import { STROKE_STYLES, LINE_ENDPOINTS } from '../../../constants/editorConstants';
import { panelStyle, titleStyle, sectionStyle, labelStyle } from '../../../styles/panelStyles';
import { createLineObject, updateLineSettings } from '../../../utils/lineUtils';

// 끝점 마커 미리보기 SVG
const MarkerPreview = ({ type }) => (
  <svg width="48" height="20" viewBox="0 0 48 20">
    {type === 'arrow' && <polygon points="8,4 0,10 8,16" fill="#374151" />}
    {type === 'circle' && <circle cx="5" cy="10" r="5" fill="#374151" />}
    {type === 'diamond' && <polygon points="5,4 10,10 5,16 0,10" fill="#374151" />}
    {type === 'square' && <rect x="0" y="5" width="10" height="10" fill="#374151" />}
    <line x1="10" y1="10" x2="38" y2="10" stroke="#374151" strokeWidth="2" />
    {type === 'arrow' && <polygon points="40,4 48,10 40,16" fill="#374151" />}
    {type === 'circle' && <circle cx="43" cy="10" r="5" fill="#374151" />}
    {type === 'diamond' && <polygon points="43,4 48,10 43,16 38,10" fill="#374151" />}
    {type === 'square' && <rect x="38" y="5" width="10" height="10" fill="#374151" />}
  </svg>
);

const LineSettingsPanel = () => {
  const { canvas, canvasSize, selectedObjects, lineSettings, setLineSettings, pushHistory, syncLayersFromCanvas } = useEditorStore();

  const selectedLineObject = selectedObjects.find(
    obj => obj.type === 'line' && !obj._isTableBorder
  );

  // 선택된 라인 객체의 설정을 패널에 반영
  useEffect(() => {
    if (!selectedLineObject) return;
    setLineSettings({
      color: selectedLineObject.stroke || '#000000',
      width: selectedLineObject.strokeWidth || 3,
      opacity: selectedLineObject.opacity ?? 1,
      strokeDashArray: selectedLineObject.strokeDashArray || null,
      marker: selectedLineObject._marker || 'none',
    });
  }, [selectedLineObject?.id]);

  // 라인 추가 (캔버스 중앙에 대각선)
  const addLineToCanvas = () => {
    if (!canvas) return;
    const size = 200;
    const cx = canvasSize.width / 2;
    const cy = canvasSize.height / 2;
    const start = { x: cx - size / 2, y: cy - size / 2 };
    const end = { x: cx + size / 2, y: cy + size / 2 };

    const newLine = createLineObject(canvas, start, end, lineSettings);
    if (newLine) canvas.setActiveObject(newLine);
    canvas.renderAll();
    syncLayersFromCanvas();
    pushHistory();
  };

  const handleSettingChange = (newSettings) => {
    setLineSettings(newSettings);

    if (!selectedLineObject || !canvas) return;

    updateLineSettings(canvas, selectedLineObject, newSettings);
    pushHistory();
  };

  const markerBtnStyle = (isSelected) => ({
    flex: '1 1 auto',
    minWidth: '55px',
    padding: '6px 4px',
    border: isSelected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
    borderRadius: '6px',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
  });

  return (
    <div style={panelStyle}>
      <h3 style={titleStyle}>{selectedLineObject ? '라인 설정' : '라인 추가'}</h3>

      {/* 라인 추가 버튼 (추가 모드) */}
      {!selectedLineObject && (
        <div style={{ ...sectionStyle, marginTop: '20px' }}>
          <label style={{ ...labelStyle, fontSize: '15px' }}>라인 추가</label>
          <button
            onClick={addLineToCanvas}
            style={{
              width: '100%',
              padding: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              backgroundColor: '#ffffff',
              cursor: 'pointer',
              fontSize: '13px',
              color: '#374151',
            }}
          >
            <svg width="40" height="20" viewBox="0 0 40 20">
              <line x1="2" y1="18" x2="38" y2="2" stroke="#3b82f6" strokeWidth="2" />
            </svg>
            클릭하여 추가
          </button>
          <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
            캔버스에서 드래그하여 직접 그릴 수도 있습니다.
          </p>
        </div>
      )}

      {/* 공통 설정 */}
      <div style={{ ...sectionStyle, marginTop: selectedLineObject ? '20px' : undefined }}>
        <ColorPicker
          label="선 색상"
          value={lineSettings.color}
          onChange={(color) => handleSettingChange({ color })}
          showGradient={false}
        />
      </div>

      <SliderInput
        label="두께"
        value={lineSettings.width}
        onChange={(width) => handleSettingChange({ width })}
        min={1}
        max={50}
        unit="px"
        showMinMax
      />

      <SliderInput
        label="투명도"
        value={Math.round(lineSettings.opacity * 100)}
        onChange={(val) => handleSettingChange({ opacity: val / 100 })}
        min={0}
        max={100}
        unit="%"
      />

      <div style={sectionStyle}>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
          선 스타일
        </label>
        <div style={{ display: 'flex', gap: '8px' }}>
          {STROKE_STYLES.map((style) => {
            const isSelected =
              (style.id === 'solid' && !lineSettings.strokeDashArray) ||
              (style.dashArray && lineSettings.strokeDashArray &&
                JSON.stringify(style.dashArray) === JSON.stringify(lineSettings.strokeDashArray));
            return (
              <button
                key={style.id}
                onClick={() => handleSettingChange({ strokeDashArray: style.dashArray })}
                style={{
                  flex: 1,
                  padding: '8px',
                  border: isSelected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                  borderRadius: '6px',
                  backgroundColor: '#ffffff',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                {style.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 끝점 마커 (양쪽 동일) */}
      <div style={sectionStyle}>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
          끝점 스타일
        </label>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {LINE_ENDPOINTS.map((ep) => (
            <button
              key={ep.id}
              onClick={() => handleSettingChange({ marker: ep.id })}
              style={markerBtnStyle(lineSettings.marker === ep.id)}
              title={ep.label}
            >
              <MarkerPreview type={ep.id} />
              <span style={{ fontSize: '10px', color: '#6b7280' }}>{ep.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 미리보기 */}
      <div style={sectionStyle}>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
          미리보기
        </label>
        <div style={{
          height: '50px',
          backgroundColor: '#f3f4f6',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '8px',
        }}>
          <svg width="100%" height="30" viewBox="0 0 200 30">
            {lineSettings.marker === 'arrow' && <polygon points="20,8 10,15 20,22" fill={lineSettings.color} opacity={lineSettings.opacity} />}
            {lineSettings.marker === 'circle' && <circle cx="15" cy="15" r="6" fill={lineSettings.color} opacity={lineSettings.opacity} />}
            {lineSettings.marker === 'diamond' && <polygon points="15,8 22,15 15,22 8,15" fill={lineSettings.color} opacity={lineSettings.opacity} />}
            {lineSettings.marker === 'square' && <rect x="9" y="9" width="12" height="12" fill={lineSettings.color} opacity={lineSettings.opacity} />}
            <line
              x1="22" y1="15" x2="178" y2="15"
              stroke={lineSettings.color}
              strokeWidth={Math.min(lineSettings.width, 6)}
              strokeDasharray={lineSettings.strokeDashArray ? lineSettings.strokeDashArray.join(',') : 'none'}
              opacity={lineSettings.opacity}
            />
            {lineSettings.marker === 'arrow' && <polygon points="180,8 190,15 180,22" fill={lineSettings.color} opacity={lineSettings.opacity} />}
            {lineSettings.marker === 'circle' && <circle cx="185" cy="15" r="6" fill={lineSettings.color} opacity={lineSettings.opacity} />}
            {lineSettings.marker === 'diamond' && <polygon points="185,8 192,15 185,22 178,15" fill={lineSettings.color} opacity={lineSettings.opacity} />}
            {lineSettings.marker === 'square' && <rect x="179" y="9" width="12" height="12" fill={lineSettings.color} opacity={lineSettings.opacity} />}
          </svg>
        </div>
      </div>
    </div>
  );
};

export default LineSettingsPanel;
